import VideoPay from '@/assets/icons/videoPay_top.svg';
import {
  fetchUserReviewDetail,
  handleDeleteReply,
  publishReplyComment,
} from '@/common-components-src/api/reviews/review.api';
import {
  anonymousData,
  PublishReplyReq,
  ReviewDetailItem,
  SecondaryReply,
} from '@/common-components-src/api/reviews/review.interface';
import PreviewResource from '@/components/previewResource';
import BottomReviewContent from '@/components/previewResource/components/BottomReviewContent';
import { ResourceTypeEnum } from '@/components/previewResource/index-type';
import TranslatePage from '@/components/translate';
import UKDialog from '@/components/UKDialog';
import UKToast from '@/components/UKToast/index';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { sendClickTrack } from '@/tracks/25217';
import { sendClickTrack as reviewDetailSendClickTrack, EXPOSURE_RECORD } from '@/tracks/25347';
import { imageUtil } from '@/utils/imageUtil';
import {
  Avatar,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import cn from 'clsx';
import { PropsWithChildren, useEffect, useState } from 'react';
import CommentBottomDetailOpts from './commentBottomDetailOpts';
import CustomTextarea from './customTextArea';
import RatingDisplay from './ratingDisplay';
import styles from './reviewDetail.module.scss';

interface Props extends PropsWithChildren<unknown> {
  lists: ReviewDetailItem[];
  skuId: string;
  onRefresh?: () => void;
  onClose?: () => void;
  onReqDetail?: () => void;
  onDeleteDetailReply?: (replyId: string) => void;
  onAnonymousChange?: (b: boolean, v: any) => void;
}

interface SecondaryProps {
  skuId: string;
  commentId: string;
  list: SecondaryReply[];
  customReplyList?: CustomReplyProps[];
  onReqDetail?: () => void;
  onDeleteReply?: (replyId: string) => void;
}

interface CustomReplyProps {
  id: string;
  commentId: string;
  userNickName: string;
  userImage: string;
  commentDate: string;
  content: string;
  replyNickname: string;
  canDelete: boolean;
}

interface ReplyDataProps {
  skuId: string;
  commentId: string;
  item: SecondaryReply;
  onReplyChange?: (value: CustomReplyProps) => void;
  onReqDetail?: () => void;
  onDeleteReply?: (replyId: string) => void;
  key: number;
}

interface CustomItemDataProps {
  item: CustomReplyProps;
}

export const SubmitButton = muiStyled(Button)(() => ({
  fontSize: '12px',
  padding: '0 8px',
  borderRadius: '4px',
  lineHeight: '28px',
  textAlign: 'center',
  fontWeight: 400,
  color: '#fff',
  background: 'linear-gradient(90deg, #FF475D 0%, #FF0F23 100%)',
  '&:hover': {
    background: 'linear-gradient(90deg, #FF475D 0%, #FF0F23 100%)',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    cursor: 'no-drop',
    color: '#fff',
  },
}));

const avatarFn = (v: any) =>
  v
    ? v.startsWith('http://') || v.startsWith('https://') || v.startsWith('//')
      ? v
      : imageUtil.concatImgUrlWithoutForceHttps(v)
    : '';
const SecondaryReplyItem = ({
  key,
  item,
  commentId,
  onReplyChange,
  skuId,
  onReqDetail,
  onDeleteReply,
}: ReplyDataProps) => {
  const { t } = useTranslation();
  const [reviewItem, setReviewItem] = useState<any>();
  const [isShowReply, setIsShowReply] = useState<boolean>();
  const [replyContent, setReplyContent] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [translateContent, setTranslateContent] = useState<{ [key: string]: string }>({});
  useEffect(() => {
    setReviewItem(item || {});
  }, [item]);

  // 添加数据验证，确保 reviewItem 存在且有必要的属性
  if (!reviewItem || !reviewItem.userImage) {
    return null; // 或者返回一个加载状态的组件
  }

  // 安全地获取数据，提供默认值
  const userImage = reviewItem.userImage || '';
  const userNickName = reviewItem.userNickName || 'Unknown User';
  const content = reviewItem.content || '';
  const replyNickname = reviewItem.replyNickname || '';
  const tag = reviewItem.tag || '';
  const canDelete = reviewItem.canDelete || false;

  // 获取指定评论项的翻译内容
  const getTranslateContent = (commentId: string) => {
    return translateContent[commentId] || '';
  };

  // 设置指定评论项的翻译内容
  const setItemTranslateContent = (commentId: string, content: string) => {
    setTranslateContent((prev) => ({
      ...prev,
      [commentId]: content,
    }));
  };

  const onTextareaChange = (value: string) => {
    if (/^[\s\n\r]*$/.test(value)) {
      setReplyContent('');
    } else {
      setReplyContent(value);
    }
  };

  const onSecondaryReply = () => {
    setIsShowReply(!isShowReply);
    sendClickTrack.Productdetail_ReviewsFloor_CommentBtn_Click();
  };
  const onSecondaryReplySubmit = async (item: SecondaryReply) => {
    const { id: targetReplyId, parentReplyId, userNickName } = item;
    const res: any = await publishReplyComment({
      targetReplyId,
      parentReplyId,
      anonymousFlag: false,
      commentId,
      content: replyContent,
    });
    const { code } = res || {};
    const { saveResult, ...rest } = res?.data || {};
    if (saveResult) {
      onReplyChange?.({
        ...rest,
        replyNickname: userNickName,
        content: replyContent,
        canDelete: true,
      } as CustomReplyProps);
      setReplyContent('');
      setIsShowReply(false);
      onReqDetail?.();
    } else {
      UKToast.show({
        icon: 'error',
        content: res?.msg,
      });
    }
  };
  const onReplyDelete = async () => {
    const res = await handleDeleteReply({ replyId: item.id, skuId });
    if (res?.data?.deleteResult) {
      onDeleteReply?.(item.id);
      handleDialogClose();
      UKToast.show({
        icon: 'success',
        content: t('common.message.deleteSuccessful'),
      });
    } else {
      UKToast.show({
        icon: 'error',
        content: t('common.message.deleteFailed'),
      });
    }
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  //回复翻译添加埋点
  const onTranslationTrack = (isclick: string) => {
    reviewDetailSendClickTrack.Evaluate_CommentDetail_TranslateBtn({
      mainskuid: skuId,
      btnStatus: isclick,
      commentId: commentId,
      index: 1,
      skuid: skuId,
    });
  };
  return (
    <div style={{ padding: '6px 0' }} className={cn(styles.child, styles['depth-2'])}>
      <div className={styles.childHeader}>
        <div className={styles.avatar}>
          <div className={styles.avatarBox}>
            <Avatar
              sizes='20px'
              src={avatarFn(userImage) || ''}
              alt=''
              sx={{
                width: 20,
                height: 20,
                bgcolor: '#E1E3E6',
                '& .MuiSvgIcon-root': {
                  width: '100%',
                  height: '100%',
                  fill: '#F0F1F2',
                },
              }}
            />
          </div>

          <span>{userNickName}</span>
          {/* <em>{item.commentDate}</em> */}
          {tag && (
            <span
              className={cn(
                styles.tag,
                tag === 'From Joybuy' && styles.tagJoy,
                tag === 'Author' && styles.tagAuthor,
              )}
            >
              {tag}
            </span>
          )}
        </div>
        {/* <div className={styles.dateTime}>{item.commentDate}</div> */}
      </div>
      <div className={styles.content}>
        <div className={styles.staticText}>
          {replyNickname && (
            <span className={styles.replyNickName}>
              {t('common.btnText.reply')}{' '}
              {replyNickname.length >= 20 ? `${replyNickname?.slice(0, 19)}...` : replyNickname}
            </span>
          )}
          <span>{getTranslateContent(commentId) || content}</span>
        </div>
        <div className={cn('flex', styles.flexBox)}>
          <div className={styles.replyBtn}>
            {canDelete && (
              <span onClick={() => setDialogOpen(true)}>{t('common.btnText.delete')}</span>
            )}
            <span onClick={() => onSecondaryReply()}>{t('common.btnText.reply')}</span>
          </div>
          {content && (
            <TranslatePage
              content={content} // 传递当前评论项的内容
              setTranslateContent={(content) => setItemTranslateContent(commentId, content)}
              onTranslationTrack={onTranslationTrack}
            />
          )}
        </div>
      </div>
      {isShowReply && (
        <div className={styles.commentReply}>
          <CustomTextarea
            value={replyContent}
            onChange={onTextareaChange}
            placeholder={t('review.commentArea')}
          />
          <p className={styles.opt}>
            <button
              className={styles.submitbtn}
              onClick={() => replyContent && onSecondaryReplySubmit(reviewItem)}
            >
              {t('common.btnText.submit')}
            </button>
          </p>
        </div>
      )}
      <UKDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        showCloseBtn={true}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        sx={{ zIndex: 2300 }}
      >
        <DialogTitle />
        <DialogContent style={{ paddingTop: '20px' }}>
          <DialogContentText id='alert-dialog-description'>
            {t('review.deleteConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} variant='outlined'>
            {t('common.btnText.cancel')}
          </Button>
          <Button onClick={onReplyDelete} autoFocus variant='contained'>
            {t('common.btnText.delete')}
          </Button>
        </DialogActions>
      </UKDialog>
    </div>
  );
};
const SecondaryReplyList = ({
  list,
  customReplyList,
  commentId,
  skuId,
  onReqDetail,
  onDeleteReply,
}: SecondaryProps) => {
  const [innerCustomReplyList, setInnerCustomReplyList] = useState<CustomReplyProps[]>([]);
  const onReplyChange = (value: CustomReplyProps) => {
    setInnerCustomReplyList([value, ...innerCustomReplyList]);
  };
  useEffect(() => {
    setInnerCustomReplyList(customReplyList || []);
  }, [customReplyList]);

  return (
    <div className={styles.secondaryReplyList}>
      {[...innerCustomReplyList, ...list]?.map((item: any, index: number) => {
        return (
          <div key={item.id || `reply-${index}`}>
            <SecondaryReplyItem
              key={item.id || `reply-${index}`}
              item={item}
              commentId={commentId}
              skuId={skuId}
              onReplyChange={onReplyChange}
              onReqDetail={onReqDetail}
              onDeleteReply={onDeleteReply}
            />
          </div>
        );
      })}
    </div>
  );
};

// 评论详情弹框
export default function ReviewDetailList({
  lists,
  skuId,
  onRefresh,
  onClose,
  onAnonymousChange,
  onDeleteDetailReply,
}: Props) {
  const { t } = useTranslation();
  const { commentOpstsStore } = useStores();
  const [isOpenReviewImage, setIsOpenReviewImage] = useState<boolean>(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('');
  const [replyContent, setReplyContent] = useState<string>('');
  const [customReplyList, setCustomReplyList] = useState<CustomReplyProps[]>([]);
  const [userLocalData, setUserLocalData] = useState<anonymousData>();
  const [translateContent, setTranslateContent] = useState<{ [key: string]: string }>({});
  const [isTranslateLoading, setIsTranslateLoading] = useState(false);

  // 获取指定评论项的翻译内容
  const getTranslateContent = (commentId: string) => {
    return translateContent[commentId] || '';
  };

  // 设置指定评论项的翻译内容
  const setItemTranslateContent = (commentId: string, content: string) => {
    setTranslateContent((prev) => ({
      ...prev,
      [commentId]: content,
    }));
  };

  const closeReviewImage = () => {
    setIsOpenReviewImage(false);
  };
  const onReqDetail = async (params: { parentReplyId: string | number; commentId: string }) => {
    const res = await fetchUserReviewDetail({
      skuId,
      ...params,
    });
    if (res?.code === '200') {
      const localCommentCount = res?.data?.replyList?.length || 0;
      commentOpstsStore.changeCommentState(params?.commentId, localCommentCount);
    }
  };
  const onDeleteReply = async (replyId: string) => {
    onDeleteDetailReply?.(replyId);
    let arr = customReplyList.filter((item) => item.id !== replyId);
    setCustomReplyList(arr);
  };
  const onReplySubmit = async (params: PublishReplyReq) => {
    const res: any = await publishReplyComment(params);
    const { code } = res || {};
    const { saveResult, ...rest } = res?.data || {};

    if (saveResult) {
      setCustomReplyList([
        {
          ...(rest as CustomReplyProps),
          content: replyContent,
          canDelete: true,
        },
        ...customReplyList,
      ]);

      setReplyContent('');
      await onReqDetail({
        parentReplyId: params.parentReplyId || 0,
        commentId: params.commentId,
      });
    } else {
      UKToast.show({
        icon: 'error',
        content: res?.msg,
      });
    }
  };

  const onTextareaChange = (value: string) => {
    if (/^[\s\n\r]*$/.test(value)) {
      setReplyContent('');
    } else {
      setReplyContent(value);
    }
  };
  const onAnonymousFn = (b: boolean, data: anonymousData) => {
    setUserLocalData(data);
    onAnonymousChange?.(b, data);
  };
  //评价详情埋点
  const onTranslationTrack = (isclick: string, skuId: string, commentId: string, key: number) => {
    reviewDetailSendClickTrack.Evaluate_CommentDetail_TranslateBtn({
      mainskuid: skuId,
      btnStatus: isclick,
      commentId: commentId,
      index: 0, //评价0 ，评论1
      skuid: skuId,
    });
  };
  return (
    <>
      <div
        className={styles.reviewListWrapper}
        id='profile_review_list'
        exptag={'exp|' + EXPOSURE_RECORD.Evaluate_CommentDetail_Expo}
        data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.01}'
        data-exptag-json-param={JSON.stringify({
          orderId: lists[0]?.orderId,
          skuid: skuId,
          commentId: lists[0]?.commentId,
          mainskuid: skuId,
        })}
      >
        {lists?.map((item: ReviewDetailItem, key: number) => {
          const {
            commentSku,
            commentDate,
            content,
            replyTreeCount,
            usefulVoteCount,
            usefulVoteResult,
            commentId,
            imageList,
            videoList,
            replyList,
            parentReplyId = 0,
            verifiedPurchase,
            commentSource,
          } = item;
          const userImage =
            userLocalData?.commentId === commentId
              ? userLocalData?.userImage || item.userImage
              : item.userImage;
          const userNickName =
            userLocalData?.commentId === commentId
              ? userLocalData?.nickName || item.userNickName
              : item.userNickName;
          const urlList = [
            ...(videoList || []).map((item: any) => ({
              type: ResourceTypeEnum.Video,
              url: item.videoUrl,
            })),
            ...(imageList || []).map((item: any) => ({
              type: ResourceTypeEnum.Image,
              url: item.imageUrl,
            })),
          ];

          return (
            <div className={styles.reviewsItemBlock} key={key}>
              <div className={styles.reviewsItem}>
                <div className={styles.reviewsAvatar}>
                  <Avatar
                    src={avatarFn(userImage) || ''}
                    alt=''
                    sx={{
                      bgcolor: '#E1E3E6',
                      '& .MuiSvgIcon-root': {
                        width: '100%',
                        height: '100%',
                        fill: '#F0F1F2',
                      },
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <p className={styles.reviewsName}>{userNickName}</p>
                    </div>
                    <div className={cn(styles.basicFont, styles.reviewsMsg)}>
                      <div className={styles.reviewsDate}>
                        <span>{commentSku?.saleAttr}</span>
                        <span>{commentDate}</span>
                        <span className='review_sign'>{commentSource}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 星+Verified Purchase标识 */}
              <div className={styles.review_sign_star}>
                <RatingDisplay value={Number(item.star)} hiddenScoreNum />
                {verifiedPurchase && (
                  <span className={styles.sign_text}>{t('review.verified')}</span>
                )}
              </div>
              <div style={{ fontSize: '14px', color: '#888B94', margin: '0 5px' }}>
                {item?.propertyExt}
              </div>
              <p className={cn(styles.basicFont, styles.reviewsContent)}>
                {getTranslateContent(commentId) || item?.content || ''}
              </p>
              <div style={{ marginBottom: '12px' }}>
                {(getTranslateContent(commentId) || item?.content) && (
                  <TranslatePage
                    content={getTranslateContent(commentId) || item?.content} // 传递当前评论项的内容
                    setTranslateContent={(content) => setItemTranslateContent(commentId, content)}
                    onTranslationTrack={(isClick: string) =>
                      onTranslationTrack(isClick, skuId, commentId, key)
                    }
                  />
                )}
              </div>
              <div className={styles.mediaZone}>
                {videoList &&
                  videoList.length > 0 &&
                  videoList?.map((v: any, index) => (
                    <div
                      key={index}
                      style={{ width: '140px', height: '140px', position: 'relative' }}
                    >
                      {/* <VideoPlayer position="top" maskType="ordinary" title={newUserNickName} content={content} src={item.videoUrl ? item.videoUrl : ""} /> */}
                      <video
                        style={{
                          width: '140px',
                          height: '140px',
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                        src={v.videoUrl ? v.videoUrl : ''}
                        onClick={() => {
                          reviewDetailSendClickTrack.Evaluate_CommentDetail_Picture({
                            material_type: '2',
                            skuid: `${skuId || '-100'}`,
                            commentId: `${commentId || '-100'}`,
                          });
                          setIsOpenReviewImage(true);
                          setCurrentImageUrl(v.videoUrl);
                        }}
                      ></video>
                      <VideoPay style={{ position: 'absolute', top: '10px', left: '10px' }} />
                    </div>
                  ))}
                {imageList?.map((_, index) => (
                  <img
                    onClick={() => {
                      reviewDetailSendClickTrack.Evaluate_CommentDetail_Picture({
                        material_type: '1',
                        skuid: `${skuId || '-100'}`,
                        commentId: `${commentId || '-100'}`,
                      });
                      setIsOpenReviewImage(true);
                      setCurrentImageUrl(_.imageUrl);
                    }}
                    key={index}
                    src={imageUtil.concatImgUrlWithoutForceHttps(_.imageUrl, 140, 0, 100)}
                    alt=''
                  />
                ))}
              </div>
              <PreviewResource
                open={isOpenReviewImage}
                defaultUrl={currentImageUrl}
                resourceList={urlList}
                closeHandler={closeReviewImage}
                bottomSlot={(currentIndex: number) => (
                  <BottomReviewContent
                    nickName={item.userNickName}
                    content={content}
                    currentIndex={currentIndex}
                    totalIndex={urlList.length}
                    closeHandler={closeReviewImage}
                    reportCommentParams={{ currentUserOperation: true }}
                  />
                )}
              />
              <CommentBottomDetailOpts
                pageType='reviewDetail'
                commentItem={item}
                skuId={skuId}
                onRefresh={onRefresh}
                onDeleteCallback={onClose}
                onAnonymousChange={onAnonymousFn}
              />
              <div className={styles.commentReply}>
                <CustomTextarea
                  value={replyContent}
                  onChange={onTextareaChange}
                  placeholder={t('review.commentArea')}
                />
                {/* <span className={styles.num}>1/1000</span> */}
                <p className={styles.opt}>
                  <button
                    className={styles.submitbtn}
                    onClick={() =>
                      replyContent &&
                      onReplySubmit({
                        commentId,
                        parentReplyId: 0,
                        content: replyContent,
                        anonymousFlag: false,
                      })
                    }
                  >
                    {t('common.btnText.submit')}
                  </button>
                </p>
              </div>
              <SecondaryReplyList
                list={replyList || []}
                customReplyList={customReplyList}
                commentId={commentId}
                skuId={skuId}
                onReqDetail={() =>
                  onReqDetail({
                    commentId,
                    parentReplyId: 0,
                  })
                }
                onDeleteReply={(replyId: string) => {
                  onDeleteReply(replyId);
                  onReqDetail({
                    commentId,
                    parentReplyId: 0,
                  });
                }}
              />
            </div>
          );
          // return renderReviewItem(item, key, false);
        })}
      </div>
    </>
  );
}
